// Guided by @docs/view-model-guidelines.md and @docs/ui-style-guidelines.md
import type { DevString } from "@phosphor/utils";
import { memo, useCallback, useState } from "react";
import { showMessageAtPointer } from "#scripts/lib/dev/showMessageAtPointer.tsx";
import { openInDevEditor } from "./openInDevEditor.ts";
import { useClickByHoverToAvoidChangingFocus } from "./useClickByHoverToAvoidChangingFocus.tsx";

export const DevStringLink = memo(({ reason }: { reason: DevString }) => {
  const {
    context: { loc } = {},
  } = reason.toJSON();

  const useLoc = loc && typeof loc === "string" ? loc.split("/").slice(-2).join("/") : null;

  // Extract the file path, excluding line/column numbers (e.g., "src/file.ts:123:45" -> "src/file.ts")
  const filePath =
    typeof loc === "string"
      ? `@${loc.replace(/(:\d+){1,2}$/, "")}` // Remove :line or :line:col at the end
      : "";

  const open = useCallback(() => openInDevEditor(typeof loc === "string" ? loc : ""), [loc]);
  const [btn, setBtn] = useState<HTMLButtonElement | null>(null);
  useClickByHoverToAvoidChangingFocus(btn, open, true);

  const copyToClipboard = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (filePath) {
        void navigator.clipboard.writeText(filePath);
        showMessageAtPointer(`Copied ${filePath} to clipboard`, e);
      }
    },
    [filePath],
  );

  if (!useLoc) return reason.toMessageString();

  return (
    <span data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:39:11" data-name="span" className="inline-flex items-center">
      <button
        data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:41:9" data-name="button" className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 bg-transparent hover:bg-neutral-100
         px-1 py-0.5 rounded-sm transition-colors duration-100 focus:outline-hidden focus-visible:ring-2
         focus-visible:ring-primary-400"
        onClick={open}
        title={useLoc}
        ref={setBtn}
        type="button"
        tabIndex={0}
        data-testid="dev-string-link-open"
        onContextMenu={copyToClipboard}
      >
        <span data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:52:15" data-name="span" className="underline underline-offset-2 decoration-dotted">{reason.toMessageString()}</span>
        <span data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:53:15" data-name="span" className="ml-0.5 text-base leading-none align-middle" aria-label="Open in editor">
          ↗︎
        </span>
      </button>
      {filePath && (
        <button
          data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:59:11" data-name="button" className="ml-1 flex items-center gap-1 cursor-pointer select-none group relative"
          onClick={copyToClipboard}
          title={`Copy: ${filePath}`}
          aria-label={`Copy file path: ${filePath}`}
          data-testid="dev-string-link-copy"
        >
          <span data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:65:17" data-name="span" className="font-mono text-neutral-400 group-hover:text-neutral-700">@</span>
          <span data-loc="phosphor-blog/scripts/lib/dev/DevStringLink.tsx:66:17" data-name="span" className="font-mono text-neutral-900 hidden group-hover:block absolute whitespace-pre left-2">
            {filePath.slice(1)}
          </span>
        </button>
      )}
    </span>
  );
});
DevStringLink.displayName = "DevStringLink";
